// Shell = sidebar + outlet. Ported from design_handoff_nice2job/app/shell.jsx.
// Hash routing replaced with react-router NavLink.
//
// Phase 14j sidebar slim-down:
//   - Removed the "You" section (Profile + Settings) — replaced by clicking
//     the avatar/workspace block at the bottom which routes to /profile.
//   - Removed the "Scanner live" status card at the bottom — was decorative
//     and confused users into thinking it was clickable navigation.
//   - Settings is still reachable at /settings; Profile page surfaces a link
//     to it so it's discoverable without a sidebar entry.

import { Link, NavLink, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, logoutSession } from '../lib/api';
import { useZh, useProductReady, useSignedIn, useOpenSignup } from '../lib/useProduct';
import { activeNavTarget } from '../lib/navActive';
import type { UserProfile } from '../lib/types';

interface SessionInfo {
  label: string | null;
  multitenant: boolean;
}

interface CVStatus { file: string | null; chars: number }

function NoCvNudge({ pathname }: { pathname: string }) {
  // Workspace-wide reminder for tenants who never uploaded a CV.
  // Hidden on /profile because the page already shows a much bigger
  // banner with the actual upload UI.
  const zh = useZh();
  const cvStatusQ = useQuery({
    queryKey: ['cv-status'],
    queryFn: () => apiGet<CVStatus>('/api/cv-status'),
    staleTime: 60 * 1000,
  });
  if (pathname === '/profile') return null;
  // Also pointless on the pages that ARE the answer to "you have no CV" —
  // generate and upload — and, in UK-CN, on the 简历 page and the board,
  // which carry their own bigger import guidance (a banner stacked on top of
  // the card saying the same thing reads as nagging).
  if (pathname === '/cv/new' || pathname === '/cv/upload') return null;
  if (zh && (pathname === '/my-cv' || pathname === '/board')) return null;
  if (cvStatusQ.isLoading) return null;
  const hasCv = !!(cvStatusQ.data && cvStatusQ.data.chars && cvStatusQ.data.chars > 100);
  if (hasCv) return null;
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 18px',
        borderBottom: '1px solid var(--line)',
        background: 'var(--pri-soft)',
        color: 'var(--ink)',
        fontSize: 13.5,
      }}
    >
      <span style={{ fontSize: 16 }}>📄</span>
      {zh ? (
        <span style={{ flex: 1 }}>
          <strong>你还没有简历。</strong>{' '}
          没有简历，职位匹配和定制都只能靠猜。{' '}
          <Link to="/cv/new" style={{ color: 'var(--pri)', fontWeight: 600 }}>用中文生成一份 →</Link>
          {' · '}
          <Link to="/cv/upload" style={{ color: 'var(--pri)', fontWeight: 600 }}>或上传已有简历</Link>
        </span>
      ) : (
        <span style={{ flex: 1 }}>
          <strong>No CV uploaded yet.</strong>{' '}
          Without a CV, evaluations fall back to a zero-shot read of every JD — noisier scores, generic tailored CVs.{' '}
          <Link to="/profile" style={{ color: 'var(--pri)', fontWeight: 600 }}>Upload it now →</Link>
        </span>
      )}
    </div>
  );
}

interface NavItem {
  to: string;
  label: string;
  count?: string | number;
}

// The UK-CN product nav: board + generate-CV up front, Chinese labels along
// the student journey. Shown only when N2J_PRODUCT=uk-cn. Deliberately trimmed
// to the student-facing surfaces — the English maintainer tools (Scanner,
// Interview prep, Offers, Outreach, Settings) are hidden from the student
// version. Account controls (profile, logout, delete account) live on the
// Profile page, reached via the avatar at the bottom of the sidebar.
const NAV_UKCN: { title: string; items: NavItem[] }[] = [
  {
    title: '求职',
    items: [
      // Shared board first: it is the only page that has content for a
      // brand-new account, since every other source is per-tenant.
      { to: '/board', label: '职位板 · Board' },
      // 简历 (/my-cv) is the identity page: the base CV + the job keywords
      // that drive the board's 为你推荐. 简历定制 (/cv) stays what it always
      // was — the per-role tailoring line. Split by user decision
      // (2026-08-14): "tailor 页不要把 CV 融进去". /cv/new and /cv/upload
      // survive as sub-pages reached from 简历.
      { to: '/my-cv', label: '简历 · CV' },
      { to: '/cv', label: '简历定制 · CV Writer' },
      { to: '/matches', label: '匹配职位 · Matches' },
    ],
  },
  {
    title: '申请',
    items: [
      { to: '/apps', label: '我的申请 · Applications' },
    ],
  },
];

// The original English product nav, shown when the flag is off. No board /
// generate-CV — those are UK-CN-only surfaces.
const NAV_DEFAULT: { title: string; items: NavItem[] }[] = [
  {
    title: 'Search',
    items: [
      { to: '/scanner', label: 'Scanner' },
      { to: '/matches', label: 'Matches' },
      { to: '/cv', label: 'CV writer' },
    ],
  },
  {
    title: 'Pipeline',
    items: [
      { to: '/apps', label: 'Applications' },
      { to: '/interview', label: 'Interview prep' },
      { to: '/offers', label: 'Offers' },
      { to: '/outreach', label: 'Outreach' },
    ],
  },
  { title: 'Workspace', items: [{ to: '/settings', label: 'Settings' }] },
];

// Routes a brand-new UK-CN user must reach BEFORE they have a profile.
// The board is the only page with content for a fresh account, and the CV
// hub (/cv) with its generate (/cv/new) and upload (/cv/upload) sub-pages
// exists precisely because the user has no English CV yet — gating them
// behind "upload your CV first" inverts the product and strands every new
// signup on an empty form.
const OPEN_WHEN_LOCKED = new Set(['/board', '/my-cv', '/cv/new', '/cv/upload']);

// Routes an ANONYMOUS visitor may see — a different question from
// OPEN_WHEN_LOCKED above (signed-in but not yet onboarded); don't merge them.
// The board is the acquisition surface: strangers arrive from the daily /news
// post and browse before deciding to register. /jobs/:id is the board's
// public detail page — parameterized, hence the prefix predicate.
const PUBLIC_ROUTES = new Set(['/board']);
const isPublicRoute = (p: string) => PUBLIC_ROUTES.has(p) || p.startsWith('/jobs/');

// Minimal chrome for anonymous visitors: logo + a signup/login CTA, in the
// landing page's header style. Deliberately renders NONE of the signed-in
// shell (Sidebar, NoCvNudge) — those components query /api/profile and
// /api/cv-status, whose 401s hard-bounce the page to /login. Keeping them
// unmounted, rather than scattering enabled: flags, is what makes the public
// board safe against the next personal query someone adds to the shell.
function PublicShell() {
  const zh = useZh();
  const openSignup = useOpenSignup();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--line)' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: 'var(--ink)' }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--ink)', color: '#fff', display: 'grid', placeItems: 'center', fontFamily: 'Fraunces, var(--cjk-serif), serif', fontWeight: 700, fontSize: 14 }}>N</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Nice2Job</div>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {openSignup && (
            <Link to="/login" style={{ fontSize: 13.5, color: 'var(--ink2)' }}>
              {zh ? '登录' : 'Sign in'}
            </Link>
          )}
          <Link to={openSignup ? '/register' : '/login'} className="btn accent">
            {zh
              ? (openSignup ? '免费注册 →' : '登录 →')
              : (openSignup ? 'Sign up free →' : 'Sign in →')}
          </Link>
        </div>
      </header>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}

function Sidebar({ locked }: { locked: boolean }) {
  const nav = useNavigate();
  const zh = useZh();
  const NAV = zh ? NAV_UKCN : NAV_DEFAULT;
  const { pathname } = useLocation();
  // 生成 (/cv/new) and 上传 (/cv/upload) keep their historical /cv/* routes
  // but BELONG to the 简历 (/my-cv) entry — CV-related lives under 简历
  // (user decision, 2026-08-14). Without this remap the longest-prefix rule
  // lights up 简历定制 (/cv) whenever you generate or upload. UK-CN only:
  // the English nav has no /my-cv and its /cv entry rightly owns both.
  const navPath = zh && ['/cv/new', '/cv/upload'].some((p) => pathname === p || pathname.startsWith(p + '/'))
    ? '/my-cv'
    : pathname;
  const activeTarget = activeNavTarget(navPath, NAV.flatMap((g) => g.items.map((i) => i.to)));
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiGet<UserProfile>('/api/profile'),
  });
  // Session info — only used to decide whether to show the "Sign out" button
  // (single-user installs don't need it, multi-tenant installs do).
  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: () => apiGet<SessionInfo>('/api/session'),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="mark">N</div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '-0.01em' }}>Nice2Job</div>
          <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
            {/* A fresh account has no name yet, which rendered a bare
                "'s workspace". Fall back to the plain label instead. */}
            {!profile
              ? (zh ? '加载中…' : 'Loading…')
              : profile.name.trim()
                ? (zh ? `${profile.name.trim().split(' ')[0]} 的工作台` : `${profile.name.split(' ')[0]}'s workspace`)
                : (zh ? '我的工作台' : 'Workspace')}
          </div>
        </div>
      </div>
      {locked && (
        <div
          style={{
            margin: '4px 8px 8px',
            padding: '10px 12px',
            border: '1px solid rgba(170,140,30,0.35)',
            background: 'rgba(170,140,30,0.10)',
            borderRadius: 6,
            fontSize: 11.5,
            lineHeight: 1.45,
            color: '#5a4408',
          }}
          title={zh ? '完善资料后解锁其余功能' : 'Finish your Profile to unlock these'}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            {zh ? '还差一步' : 'Setup needed'}
          </div>
          {zh
            ? '「职位板」和「简历」现在就能用。填好下方资料后解锁其余功能。'
            : 'Save your Profile below to unlock the workspace.'}
        </div>
      )}
      {NAV.map((group) => (
        <div key={group.title}>
          <div className="tab" style={{ padding: '14px 12px 6px' }}>
            {group.title}
          </div>
          {group.items.map((n) =>
            locked && !(zh && OPEN_WHEN_LOCKED.has(n.to)) ? (
              <span
                key={n.to}
                className="nav-link"
                title={zh ? '完善资料后解锁' : 'Finish your Profile to unlock this'}
                style={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                <span>{n.label}</span>
                {n.count != null && <span className="count">{n.count}</span>}
              </span>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                // `end` makes NavLink's own matching exact, which is what
                // aria-current="page" should mean: on /apps/123 the /apps link
                // does not point at the current page. Without it NavLink marked
                // both /cv and /cv/new as the current page at /cv/new.
                end
                // The className MUST stay a function. Given a string, NavLink
                // appends its own "active" on top of whatever you pass — which
                // silently reintroduces the prefix-matching bug this is here to
                // fix (observed live: class="nav-link  active" on 简历定制 at
                // /cv/new). The function form hands className over completely.
                // isActive is deliberately unused: see activeNavTarget().
                className={() => `nav-link ${n.to === activeTarget ? 'active' : ''}`}
              >
                <span>{n.label}</span>
                {n.count != null && <span className="count">{n.count}</span>}
              </NavLink>
            )
          )}
        </div>
      ))}
      {/* Bottom-left: avatar block is now the entry point to Profile.
          (Phase 14j: replaced separate "You · Profile + Settings" nav group
          with this single click target — Settings is reachable from inside
          the Profile page.) */}
      <div style={{ marginTop: 'auto', padding: '12px 4px 0' }}>
        {profile && (
          <button
            type="button"
            onClick={() => nav('/profile')}
            title={zh ? '打开个人资料' : 'Open Profile (and access Settings from there)'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 8px',
              width: '100%',
              border: '1px solid var(--line)',
              borderRadius: 8,
              background: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 120ms',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.02)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <div className="avatar">{profile.initials || profile.name.slice(0, 2).toUpperCase()}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>{profile.name}</div>
              <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                {session?.multitenant && session.label
                  ? (zh ? `已登录：${session.label}` : `Signed in as ${session.label}`)
                  : (zh ? '本地工作台' : 'Local workspace')}
              </div>
            </div>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }} aria-hidden>›</span>
          </button>
        )}
        {/* Sign-out only shown when MULTITENANT is active — single-user
            installs don't have a session to sign out from. */}
        {session?.multitenant && (
          <button
            type="button"
            onClick={() => {
              if (confirm(zh
                ? '退出登录？你的数据会保留在服务器上，重新登录即可继续。'
                : 'Sign out? Your data stays on the server — just log back in to continue.')) {
                logoutSession();
              }
            }}
            style={{
              marginTop: 8,
              display: 'block',
              width: '100%',
              padding: '6px 8px',
              fontSize: 11.5,
              color: 'var(--ink3)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--ink)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--ink3)')}
          >
            {zh ? '退出登录 ↗' : 'Sign out ↗'}
          </button>
        )}
      </div>
    </aside>
  );
}

export default function Shell() {
  const ready = useProductReady();
  const signedIn = useSignedIn();
  // Brand-new tenants land on /profile so they can upload a CV and let
  // Ollama auto-fill the contact block before doing anything else. The
  // /profile page detects onboarded=false and renders a prominent
  // "upload CV → auto-fill → save" guide. The legacy /onboarding wizard
  // is still reachable for users who want the multi-step flow, but it
  // is no longer the forced default.
  //
  // enabled gates the query on identity: an anonymous visitor on the public
  // board must never fire /api/profile — its 401 hard-bounces to /login.
  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile-onboarded-check'],
    queryFn: () => apiGet<UserProfile>('/api/profile'),
    staleTime: 30 * 1000,
    enabled: ready && signedIn,
  });
  const location = useLocation();
  const zh = useZh();
  // Quiet blank while /api/session is in flight — the Landing.tsx precedent.
  // Deciding signed-in vs anonymous before the answer arrives would flash the
  // wrong chrome (or bounce a valid session to /login) on every hard refresh.
  if (!ready) {
    return <div style={{ minHeight: '100vh', background: 'var(--bg)' }} />;
  }
  if (!signedIn) {
    if (isPublicRoute(location.pathname)) return <PublicShell />;
    return <Navigate to="/login" replace />;
  }
  // Avoid bouncing /profile itself — the user is already there setting
  // up. In UK-CN the board and 生成简历 are also reachable while locked
  // (see OPEN_WHEN_LOCKED). Other workspace routes still redirect. The
  // board's detail pages inherit the board's reachability — without this, a
  // fresh signup clicking any row got bounced to /profile mid-browse.
  const notOnboarded = !isLoading && profile && profile.onboarded === false;
  const reachableWhileLocked =
    location.pathname === '/profile'
    || (zh && (OPEN_WHEN_LOCKED.has(location.pathname) || location.pathname.startsWith('/jobs/')));
  if (notOnboarded && !reachableWhileLocked) {
    return <Navigate to="/profile" replace />;
  }
  return (
    <div className="layout">
      <Sidebar locked={!!notOnboarded} />
      <main className="main">
        <NoCvNudge pathname={location.pathname} />
        <Outlet />
      </main>
    </div>
  );
}
